import { FC, useCallback, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { z } from 'zod';
import { CheckboxInput } from 'src/components/checkbox_input';
import { EMAIL_SCHEMA } from 'src/utils';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RECIPIENT_DELIMITER = /[\s,]+$/;

const PERSONAL_EMAIL_DOMAINS = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'aol.com',
  'icloud.com',
  'live.com',
  'msn.com',
  'protonmail.com',
  'mail.com',
  'zoho.com',
] as const;

function isPersonalEmail(email: string): boolean {
  const domain = email.split('@').pop()?.toLowerCase() ?? '';

  if (!domain) {
    return false;
  }

  return PERSONAL_EMAIL_DOMAINS.some(
    (d) => d === domain || domain.endsWith('.' + d),
  );
}

/**
 * Validates, normalizes (lowercase), and deduplicates email strings.
 */
function parseRecipients(values: unknown[]): string[] {
  return values
    .filter((v): v is string => typeof v === 'string')
    .map((s) => s.trim().toLowerCase())
    .filter((e) => EMAIL_REGEX.test(e))
    .filter((e, i, a) => a.indexOf(e) === i);
}

const shareTestReportSchema = z.object({
  recipients: z
    .array(EMAIL_SCHEMA)
    .min(1, 'Please enter at least one email address')
    .refine((emails) => !emails.some((e) => isPersonalEmail(e)), {
      message:
        'Personal email addresses are not allowed. Please use a valid work email.',
    }),
  acknowledgeConsent: z.boolean().refine((val) => val === true, {
    message: 'You must acknowledge and consent to share reports',
  }),
  confirmAccuracy: z.boolean().refine((val) => val === true, {
    message: 'You must confirm email addresses are accurate',
  }),
});

export type ShareTestReportFormData = z.infer<typeof shareTestReportSchema>;

interface ShareTestReportDialogProps {
  open: boolean;
  onClose: () => void;
  onShare: (data: ShareTestReportFormData) => void;
}

const ACKNOWLEDGE_CONSENT_LABEL = (
  <Typography variant='body2'>
    I acknowledge and consent to share these reports with the listed recipients,
    in accordance with the Terms of Service, Privacy Policy, and HIPAA Notice.
  </Typography>
);

const CONFIRM_ACCURACY_LABEL = (
  <Typography variant='body2'>
    I confirm that all email addresses entered are accurate and belong to the
    intended recipients.
  </Typography>
);

/**
 * Dialog for sharing test reports. Recipients are added by pressing Enter,
 * typing a comma/space, blurring, or pasting a comma-separated list.
 */
export const ShareTestReportDialog: FC<ShareTestReportDialogProps> = ({
  open,
  onClose,
  onShare,
}) => {
  const [inputValue, setInputValue] = useState('');
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ShareTestReportFormData>({
    resolver: zodResolver(shareTestReportSchema),
    mode: 'onChange',
    defaultValues: {
      recipients: [],
      acknowledgeConsent: false,
      confirmAccuracy: false,
    },
  });

  const onSubmit = useCallback(
    (data: ShareTestReportFormData) => {
      onShare(data);
      setInputValue('');
      reset();
    },
    [onShare, reset],
  );

  const handleClose = useCallback(() => {
    setInputValue('');
    reset();
    onClose();
  }, [onClose, reset]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      data-testid='share-test-report-dialog'
    >
      <DialogTitle
        sx={{ textAlign: 'left' }}
        id='share-test-report-dialog-title'
      >
        Add Report Recipient(s)
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ p: 0 }}>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Controller
              name='recipients'
              control={control}
              render={({ field }) => {
                const recipients = field.value || [];
                return (
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={recipients}
                    inputValue={inputValue}
                    onInputChange={(_, newInputValue, reason) => {
                      // When the user types a comma or space at the end, treat
                      // that as "add the current text as a recipient" (same as
                      // pressing Enter). Skip when reason is 'reset' (e.g. MUI
                      // clearing after Enter).
                      if (
                        reason !== 'reset' &&
                        RECIPIENT_DELIMITER.test(newInputValue)
                      ) {
                        const textBeforeDelimiter = newInputValue.replace(
                          RECIPIENT_DELIMITER,
                          '',
                        );
                        field.onChange(
                          parseRecipients([...recipients, textBeforeDelimiter]),
                        );
                        setInputValue('');
                      } else {
                        setInputValue(newInputValue);
                      }
                    }}
                    onChange={(_, newValue) =>
                      field.onChange(parseRecipients(newValue as unknown[]))
                    }
                    onBlur={() => {
                      if (!inputValue) return;
                      field.onChange(
                        parseRecipients([...recipients, inputValue]),
                      );
                      setInputValue('');
                    }}
                    filterOptions={() => []}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Recipients' email addresses"
                        placeholder='Type an email and press enter'
                        error={!!errors.recipients}
                        helperText={errors.recipients?.message || ' '}
                        slotProps={{
                          ...params.slotProps,
                          htmlInput: {
                            ...params.slotProps?.htmlInput,
                            'data-testid': 'share-test-report-recipients-input',
                          },
                          inputLabel: { shrink: true },
                        }}
                      />
                    )}
                    slotProps={{ chip: { size: 'small' } }}
                  />
                );
              }}
            />
            <Controller
              name='acknowledgeConsent'
              control={control}
              render={({ field }) => (
                <CheckboxInput
                  checked={field.value}
                  onChange={field.onChange}
                  data-testid='share-test-report-acknowledge-consent'
                  label={ACKNOWLEDGE_CONSENT_LABEL}
                  error={!!errors.acknowledgeConsent}
                  errorText={errors.acknowledgeConsent?.message}
                  required
                />
              )}
            />
            <Controller
              name='confirmAccuracy'
              control={control}
              render={({ field }) => (
                <CheckboxInput
                  checked={field.value}
                  onChange={field.onChange}
                  data-testid='share-test-report-confirm-accuracy'
                  label={CONFIRM_ACCURACY_LABEL}
                  error={!!errors.confirmAccuracy}
                  errorText={errors.confirmAccuracy?.message}
                  required
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-end', gap: 2 }}>
          <Button
            onClick={handleClose}
            variant='text'
            size='large'
            data-testid='share-test-report-close-button'
          >
            CLOSE
          </Button>
          <Button
            type='submit'
            variant='contained'
            size='large'
            data-testid='share-test-report-share-button'
          >
            SHARE
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
